import { useState } from "react"
import { Link2, Loader2 } from "lucide-react"
import type { AppShell } from "@/App"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { autoLinkCatalog, type CatalogAutoLinkResult } from "@/lib/api"

/**
 * «Ligar ao CRM» — as linhas do catálogo AC/BC sem produto do CRM ficam sem
 * referência no painel e sem foto nem ficha no PDF. Primeiro mostra o que vai
 * ligar (simulação); só grava depois de confirmar. Ambíguas e sem
 * correspondência ficam listadas para ligar à mão (pedido da Mais Energia, 02/11).
 */
export function AutoLinkButton({ shell, kind, onDone }: { shell: AppShell | null; kind: "ac" | "bc"; onDone: () => void }) {
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [preview, setPreview] = useState<CatalogAutoLinkResult | null>(null)

  if (!shell?.isAdmin) return null

  const run = async (dryRun: boolean) => {
    setBusy(true)
    try {
      const r = await autoLinkCatalog(shell, kind, dryRun)
      if (dryRun) {
        setPreview(r)
        setOpen(true)
      } else {
        shell.bridge.toast({ variant: "success", description: `${r.linked.length} linha(s) ligadas ao CRM` })
        setOpen(false)
        setPreview(null)
        onDone()
      }
    } catch (e) {
      shell.bridge.toast({ variant: "error", description: e instanceof Error ? e.message : "Falha ao ligar ao CRM" })
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => run(true)} disabled={busy}>
        {busy && !open ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Link2 className="mr-1 h-4 w-4" />}
        Ligar ao CRM
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Ligar o catálogo aos produtos do CRM</DialogTitle>
          </DialogHeader>
          {preview && (
            <div className="space-y-3 text-sm">
              <p className="text-muted-foreground">
                As linhas ligadas passam a mostrar a referência do Odoo e o PDF a foto e a ficha técnica do produto.
                {preview.already > 0 && ` ${preview.already} já estavam ligadas.`}
              </p>
              <Section title={`Vão ser ligadas (${preview.linked.length})`}>
                {preview.linked.map((l) => (
                  <li key={String(l.id)}>
                    {l.name} → <span className="font-mono text-xs">{l.sku ?? `#${l.product_id}`}</span>
                    <span className="text-muted-foreground"> ({l.by === "sku" ? "pela referência" : "pelo nome"})</span>
                  </li>
                ))}
              </Section>
              <Section title={`Mais do que um produto com o mesmo nome — ligar à mão (${preview.ambiguous.length})`}>
                {preview.ambiguous.map((a) => (
                  <li key={String(a.id)}>
                    {a.name}: {a.candidates.map((c) => c.sku ?? `#${c.id}`).join(", ")}
                  </li>
                ))}
              </Section>
              <Section title={`Sem produto no CRM com este nome (${preview.not_found.length})`}>
                {preview.not_found.map((n) => (
                  <li key={String(n.id)}>{n.name}</li>
                ))}
              </Section>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} disabled={busy}>Cancelar</Button>
            <Button onClick={() => run(false)} disabled={busy || !preview || preview.linked.length === 0}>
              {busy ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
              Ligar {preview?.linked.length ?? 0}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  const empty = Array.isArray(children) && children.length === 0
  if (empty) return null
  return (
    <div>
      <p className="font-medium">{title}</p>
      <ul className="mt-1 max-h-48 list-disc space-y-0.5 overflow-y-auto pl-5 text-xs">{children}</ul>
    </div>
  )
}
